import { PaymentMethodBrand, PaymentMethodType } from '@aiwyn/api';

export enum PaymentMethodMode {
  SAVED = 'Saved',
  NEW = 'New',
  TEMPORARY = 'Temporary',
  SET_UP_LATER = 'Set up later',
  NONE = 'None',
}

export enum PaymentMethodIntent {
  PAYMENT = 'payment',
  SETUP = 'setup',
}

export enum PaymentFlow {
  STANDARD = 'standard',
  PLAN = 'plan',
}

export const PAYMENT_METHOD_BRAND_DISPLAY: Record<PaymentMethodBrand, string> = {
  [PaymentMethodBrand.AMEX]: 'American Express',
  [PaymentMethodBrand.BANK]: 'Bank Account',
  [PaymentMethodBrand.DINERS]: 'Diners Club',
  [PaymentMethodBrand.DISCOVER]: 'Discover',
  [PaymentMethodBrand.JCB]: 'JCB',
  [PaymentMethodBrand.MASTERCARD]: 'Mastercard',
  [PaymentMethodBrand.UNIONPAY]: 'UnionPay',
  [PaymentMethodBrand.VISA]: 'Visa',
  [PaymentMethodBrand.UNKNOWN]: 'Unknown',
};
export const PAYMENT_METHOD_TYPE_DISPLAY: Record<Partial<PaymentMethodType>, string> = {
  [PaymentMethodType.CARD]: 'Card',
  [PaymentMethodType.ACSS_DEBIT]: 'Canadian pre-authorized debit',
  [PaymentMethodType.BACS_DEBIT]: 'Bacs direct debit',
  [PaymentMethodType.BACS]: 'Bacs direct debit',
  [PaymentMethodType.US_BANK_ACCOUNT]: 'US bank account',
  // TODO: See if we can get these removed by getting raw values for type from backend.
  [PaymentMethodType.ACH]: 'US bank account',
  [PaymentMethodType.ACSS]: 'Canadian pre-authorized debit',
  [PaymentMethodType.GOOGLE_PAY]: 'Google Pay',
  [PaymentMethodType.APPLE_PAY]: 'Apple Pay'
};

export enum PaymentMethodDisplayName {
  CREDIT_CARD = 'creditCard',
  US_BANK_ACCOUNT = 'usBankAccount',
  CANADIAN_PRE_AUTHORIZED_DEBIT = 'canadianPreAuthorizedDebit',
  BACS = 'bacs',
  GOOGLE_PAY = 'googlePay',
  APPLE_PAY = 'applePay'
}
export enum PAYMENT_METHOD_CATEGORY {
  CARD = 'card',
  BANK_DEBIT = 'bank_debit',
  WALLET = 'wallet',
}

export const PAYMENT_METHOD_CATEGORY_LOG_NAME: Record<PAYMENT_METHOD_CATEGORY, string> = {
  [PAYMENT_METHOD_CATEGORY.BANK_DEBIT]: 'Bank Debit',
  [PAYMENT_METHOD_CATEGORY.CARD]: 'Card',
  [PAYMENT_METHOD_CATEGORY.WALLET]: 'Wallet'
}

export const PAYMENT_METHOD_TYPE_TO_CATEGORY: Record<Partial<PaymentMethodType>, PAYMENT_METHOD_CATEGORY> = {
  [PaymentMethodType.CARD]: PAYMENT_METHOD_CATEGORY.CARD,
  [PaymentMethodType.ACSS_DEBIT]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.BACS]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.BACS_DEBIT]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.US_BANK_ACCOUNT]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.ACH]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.ACSS]: PAYMENT_METHOD_CATEGORY.BANK_DEBIT,
  [PaymentMethodType.GOOGLE_PAY]: PAYMENT_METHOD_CATEGORY.WALLET,
  [PaymentMethodType.APPLE_PAY]: PAYMENT_METHOD_CATEGORY.WALLET,
};

export const PAYMENT_METHOD_TYPE_TO_DISPLAY_NAMES: Record<Partial<PaymentMethodType>, string> = {
  [PaymentMethodType.CARD]: PaymentMethodDisplayName.CREDIT_CARD,
  [PaymentMethodType.ACSS_DEBIT]: PaymentMethodDisplayName.CANADIAN_PRE_AUTHORIZED_DEBIT,
  [PaymentMethodType.BACS]: PaymentMethodDisplayName.BACS,
  [PaymentMethodType.BACS_DEBIT]: PaymentMethodDisplayName.BACS,
  [PaymentMethodType.US_BANK_ACCOUNT]: PaymentMethodDisplayName.US_BANK_ACCOUNT,
  [PaymentMethodType.ACH]: PaymentMethodDisplayName.US_BANK_ACCOUNT,
  [PaymentMethodType.ACSS]: PaymentMethodDisplayName.CANADIAN_PRE_AUTHORIZED_DEBIT,
  [PaymentMethodType.GOOGLE_PAY]: PaymentMethodDisplayName.GOOGLE_PAY,
  [PaymentMethodType.APPLE_PAY]: PaymentMethodDisplayName.APPLE_PAY,
};
export const PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED = [
  PaymentMethodType.CARD,
  PaymentMethodType.BACS,
  PaymentMethodType.BACS_DEBIT,
];

// Direct-debit mandates Stripe will not create without a billing email. A guest has no account
// email, so the in-element email field must render for these so they can supply one.
export const PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED = [
  PaymentMethodType.ACSS,
  PaymentMethodType.ACSS_DEBIT,
  PaymentMethodType.BACS,
  PaymentMethodType.BACS_DEBIT,
];

// Show the Stripe Payment Element's own email field only when the selected method needs a billing
// email for its mandate AND the buyer hasn't already supplied one (sidebar or account); otherwise the
// email travels via billing details and the in-element field stays hidden. Shared by the element's
// initialize and selection-change paths so the two cannot drift.
export const shouldCollectEmailInElement = (requiresBillingEmail: boolean, billingEmail?: string): boolean =>
  requiresBillingEmail && !billingEmail;

export const isAWalletPaymentMethodType = (paymentMethodType: PaymentMethodType) =>
  PAYMENT_METHOD_TYPE_TO_CATEGORY[paymentMethodType] === PAYMENT_METHOD_CATEGORY.WALLET;
